import { Show, type Component, type JSX } from 'solid-js';
import { Panel } from '~ui/panel';
import { Heading, Text } from '~ui/text';
import { formatCurrency, formatPercent, perYear } from '~/lib/format';

// The figures worth reading before anything else, across every account.
//
// The borrowed side of the book — debt, what it costs, what it nets — lives on
// the Loans page, beside the loans it is about.
//
// Numbers, not charts. Each is a single current value, and a plot of one number
// is a worse way to show it than the number — the trend is what the History and
// Project pages are for.
//
// Both income figures are monthly. This is a book built to be lived off, so
// "what does it pay me this month" is the question actually being asked; the
// annual sits underneath each, because a monthly figure with no annual beside it
// is the one people mis-scale by twelve.
//
// Proportional figures rather than tabular: these are display sizes and nothing
// sits underneath them to align to, so equal-width digits only make the number
// look loose.

// One block, no bordered header. The label keeps the same eyebrow it had as a
// section title — it is only the row and the rule under it that are gone: a rule
// divides a header from content, and there is no content here to divide it from,
// since the card is three lines of one thought.
export const Card: Component<{ label: string; value: string; tone?: 'green' | 'red'; children?: JSX.Element }> = (props) => (
    <Panel>
        <div class='flex flex-1 flex-col justify-center gap-0.5 px-4 py-3'>
            <Heading level={2} class='truncate'>
                {props.label}
            </Heading>
            <Text tone={props.tone} weight='medium' class='text-2xl tracking-tight'>
                {props.value}
            </Text>
            <Show when={props.children}>
                <Text tone='muted' class='truncate'>
                    {props.children}
                </Text>
            </Show>
        </div>
    </Panel>
);

/**
 * The three figures, given rather than fetched.
 *
 * Both the real book and the Sandbox show this row, and they compute their
 * numbers from different places — one from a blotter of trades, the other from
 * amounts typed straight in. Passing them in is what keeps that a difference of
 * source and not a difference of card: two hand-kept copies of this row would
 * drift, and a Sandbox whose cards disagree with the book's for the same
 * holdings is worse than no Sandbox.
 *
 * Annual figures in, monthly derived here, so no caller can divide by twelve
 * differently from another. Everything is pre-tax: the rate depends on the
 * account, on the character of each distribution and on your other income, so
 * nothing here has ever had tax applied.
 */
export interface BookFigures {
    incomePerYear: number | null;
    /** Distributions less loan interest. */
    netPerYear: number | null;
    yieldPct: number | null;
    value: number | null;
    /** What sits under the value. Cost base for the real book; a sandbox has no
        cost base worth stating, since every position was bought at today's
        price, so it says what is owed against it instead. */
    valueNote: JSX.Element;
}


const perMonth = (annual: number | null): number | null => (annual == null ? null : annual / 12);

export const Cards: Component<{ figures: BookFigures }> = (props) => {
    const net = () => perMonth(props.figures.netPerYear);
    return (
        <div class='flex gap-px border-b bg-border'>
            <div class='min-w-0 flex-1'>
                {/* What the book pays in a month at the current rate — the figure
                    the whole thing exists to produce. */}
                <Card label='Dividends per month' value={formatCurrency(perMonth(props.figures.incomePerYear))} tone='green'>
                    {perYear(props.figures.incomePerYear)} · {formatPercent(props.figures.yieldPct, 2)} yield
                </Card>
            </div>
            <div class='min-w-0 flex-1'>
                {/* The same money after the cost of borrowing it. Identical to the
                    card on its left until there is a loan, and the gap between the
                    two is the whole argument for or against the leverage. */}
                <Card label='Net dividends per month' value={formatCurrency(net())} tone={net() == null ? undefined : net()! >= 0 ? 'green' : 'red'}>
                    {perYear(props.figures.netPerYear)}
                </Card>
            </div>
            <div class='min-w-0 flex-1'>
                {/* Market value of everything, cash included. */}
                <Card label='NAV' value={formatCurrency(props.figures.value)}>{props.figures.valueNote}</Card>
            </div>
        </div>
    );
};

export default Cards;
